import { useState, useEffect } from "react";
import axios from "axios";
import { OrderImportanceBadge } from "@/components/products/OrderImportanceBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FileSpreadsheet, Package, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReorderReport = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    fetchReorderProducts();
  }, []);

  const fetchReorderProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/reorder/needed`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching reorder products:", error);
      toast.error("Failed to load reorder report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/reorder-report`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `reorder_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report exported successfully");
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === products.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(products.map(p => p.id)));
    }
  };

  const removeSelected = () => {
    const count = selected.size;
    setProducts(prev => prev.filter(p => !selected.has(p.id)));
    setSelected(new Set());
    toast.success(`Removed ${count} item${count > 1 ? "s" : ""} from list`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading report...</div>
      </div>
    );
  }

  return (
    <div data-testid="reorder-report-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reorder Report</h1>
          <p className="text-sm text-gray-500">Products below reorder level requiring action</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="destructive" size="sm" onClick={removeSelected} data-testid="remove-selected-btn">
              <Trash2 className="h-4 w-4 mr-1" />
              Remove {selected.size}
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleExport}
            disabled={exporting || products.length === 0}
            data-testid="export-excel-button"
            className="bg-green-600 hover:bg-green-700"
          >
            {exporting ? "Exporting..." : <><Download className="h-4 w-4 mr-1" /> Export to Excel</>}
          </Button>
        </div>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500">No products require reordering at this time</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="flex items-center text-base">
              <FileSpreadsheet className="h-5 w-5 mr-2 text-purple-600" />
              Items Requiring Reorder ({products.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reorder-table">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="pl-4 pr-1 py-2 text-left w-8">
                      <Checkbox
                        checked={selected.size === products.length && products.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Product</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Category</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Current</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">On Order</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Min</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Reorder</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Max</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Order Qty</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Supplier</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((product) => {
                    const stockOnOrder = product.stock_on_order || 0;
                    const effectiveStock = product.current_stock + stockOnOrder;
                    const orderQuantity = Math.max(0, product.maximum_level - effectiveStock);
                    const isCritical = product.current_stock <= product.minimum_level;
                    const isSelected = selected.has(product.id);

                    return (
                      <tr
                        key={product.id}
                        data-testid={`reorder-row-${product.id}`}
                        className={`${isCritical ? "bg-red-50/60" : "hover:bg-gray-50"} ${isSelected ? "bg-purple-50" : ""} cursor-pointer`}
                        onClick={() => toggleSelect(product.id)}
                      >
                        <td className="pl-4 pr-1 py-1.5" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(product.id)}
                            data-testid={`select-${product.id}`}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-1.5">
                            {isCritical && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                            <span className="font-medium text-gray-900 leading-tight">{product.name}</span>
                            <OrderImportanceBadge level={product.order_importance} compact testId={`reorder-importance-${product.id}`} />
                            {isCritical && <span className="text-[10px] text-red-600 font-semibold">CRITICAL</span>}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-gray-600 text-xs">{product.category}</td>
                        <td className="px-3 py-1.5 text-right">
                          <span className={`font-medium ${isCritical ? "text-red-600" : "text-orange-600"}`}>
                            {product.current_stock}
                          </span>
                          <span className="text-gray-400 text-xs ml-0.5">{product.unit}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {stockOnOrder > 0 ? (
                            <span className="font-medium text-blue-600">{stockOnOrder}</span>
                          ) : (
                            <span className="text-gray-300">0</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-600">{product.minimum_level}</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">{product.reorder_level}</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">{product.maximum_level}</td>
                        <td className="px-3 py-1.5 text-right">
                          <span className="font-bold text-purple-600" data-testid={`order-qty-${product.id}`}>
                            {orderQuantity}
                          </span>
                          <span className="text-gray-400 text-xs ml-0.5">{product.unit}</span>
                        </td>
                        <td className="px-3 py-1.5 text-gray-600 text-xs">{product.supplier || "N/A"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReorderReport;
